import { SessionAttributionType, Platform, base_events } from '@superbet-group/product.analytics';

function createError(message) {
    const err = new Error(message);
    err.source = "ulid";
    return err;
}
// These values should NEVER change. If
// they do, we're no longer making ulids!
const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford's Base32
const ENCODING_LEN = ENCODING.length;
const TIME_MAX = Math.pow(2, 48) - 1;
const TIME_LEN = 10;
const RANDOM_LEN = 16;
function randomChar(prng) {
    let rand = Math.floor(prng() * ENCODING_LEN);
    if (rand === ENCODING_LEN) {
        rand = ENCODING_LEN - 1;
    }
    return ENCODING.charAt(rand);
}
function encodeTime(now, len) {
    if (isNaN(now)) {
        throw new Error(now + " must be a number");
    }
    if (now > TIME_MAX) {
        throw createError("cannot encode time greater than " + TIME_MAX);
    }
    if (now < 0) {
        throw createError("time must be positive");
    }
    if (Number.isInteger(Number(now)) === false) {
        throw createError("time must be an integer");
    }
    let mod;
    let str = "";
    for (; len > 0; len--) {
        mod = now % ENCODING_LEN;
        str = ENCODING.charAt(mod) + str;
        now = (now - mod) / ENCODING_LEN;
    }
    return str;
}
function encodeRandom(len, prng) {
    let str = "";
    for (; len > 0; len--) {
        str = randomChar(prng) + str;
    }
    return str;
}
function detectPrng(allowInsecure = false, root) {
    if (!root) {
        root = typeof window !== "undefined" ? window : null;
    }
    const browserCrypto = root && (root.crypto || root.msCrypto);
    if (browserCrypto) {
        return () => {
            const buffer = new Uint8Array(1);
            browserCrypto.getRandomValues(buffer);
            return buffer[0] / 0xff;
        };
    }
    else {
        try {
            const nodeCrypto = require("crypto");
            return () => nodeCrypto.randomBytes(1).readUInt8() / 0xff;
        }
        catch (e) { }
    }
    if (allowInsecure) {
        try {
            console.error("secure crypto unusable, falling back to insecure Math.random()!");
        }
        catch (e) { }
        return () => Math.random();
    }
    throw createError("secure crypto unusable, insecure Math.random not allowed");
}
function factory(currPrng) {
    if (!currPrng) {
        currPrng = detectPrng();
    }
    return function ulid(seedTime) {
        if (isNaN(seedTime)) {
            seedTime = Date.now();
        }
        return encodeTime(seedTime, TIME_LEN) + encodeRandom(RANDOM_LEN, currPrng);
    };
}
const ulid = factory();

const JurisdictionsMap = {
    'ro': [
        {
            brand: 'superbet',
            channel: 'online',
            country: 'RO',
            domain: 'ro',
            licence: 'RO'
        }
    ],
    'pl': [
        {
            brand: 'superbet',
            channel: 'online',
            country: 'PL',
            domain: 'pl',
            licence: 'PL'
        }
    ],
    'br': [
        {
            brand: 'superbet',
            channel: 'online',
            country: 'BR',
            domain: 'br',
            licence: 'SPA'
        },
    ],
    'com': [
        {
            brand: 'superbet',
            channel: 'online',
            country: 'BR',
            domain: 'com',
            licence: 'MGA'
        }
    ],
    'be': [
        {
            brand: 'napoleonsports',
            channel: 'online',
            country: 'BE',
            domain: 'be',
            licence: 'BGC'
        },
        {
            brand: 'napoleoncasino',
            channel: 'online',
            country: 'BE',
            domain: 'be',
            licence: 'BGC'
        },
        {
            brand: 'napoleondice',
            channel: 'online',
            country: 'BE',
            domain: 'be',
            licence: 'BGC'
        },
        {
            brand: 'napoleongames',
            channel: 'online',
            country: 'BE',
            domain: 'be',
            licence: 'BGC'
        }
    ],
    'rs': [
        {
            brand: 'superbet',
            channel: 'online',
            country: 'RS',
            domain: 'rs',
            licence: 'RS'
        }
    ],
    'bg': [
        {
            brand: 'superbet',
            channel: 'online',
            country: 'BG',
            domain: 'bg',
            licence: 'BG'
        }
    ]
};

// Note: Do not change these.
const DEVICE_ID_KEY = 'device_id'; // Note: this is shared with `dot-country-web.client` and `vue-analytics-plugin`. 
const SESSION_ID_KEY = '_sbaSessionId'; // Note: this is shared with `vue-analytics-plugin`.
const visibilityImplementations = [
    { hiddenPropertyName: 'hidden', eventName: 'visibilitychange' },
    { hiddenPropertyName: 'mozHidden', eventName: 'mozvisibilitychange' },
    { hiddenPropertyName: 'webkitHidden', eventName: 'webkitvisibilitychange' },
    { hiddenPropertyName: 'msHidden', eventName: 'msvisibilitychange' },
];
function flattenObject(obj, excluded = [], delimeter = '.', parentKey = '') {
    return Object.keys(obj)
        .filter((k) => !excluded.includes(k))
        .reduce((acc, key) => {
        const fullKey = parentKey ? `${parentKey}${delimeter}${key}` : key;
        const value = obj[key];
        // Deal with arrays, postfix them with the index
        if (Array.isArray(value)) {
            const arrayValues = value
                .reduce((innerAcc, item, index) => ({
                ...innerAcc,
                ...flattenObject(item, excluded, delimeter, `${fullKey}${delimeter}${index}`),
            }), {});
            return {
                ...acc,
                ...arrayValues,
            };
        }
        // Deal with objects, just flatten nested ones
        if (typeof value === 'object' && value !== null) {
            return {
                ...acc,
                ...flattenObject(value, excluded, delimeter, fullKey),
            };
        }
        // Everything else we append without any manipulation at all.
        return {
            ...acc,
            [fullKey]: value,
        };
    }, {});
}
function isMobileViewport() {
    const desktopViewport = 1280;
    const screenWidth = window.innerWidth;
    return screenWidth < desktopViewport;
}
function getPlatform() {
    if (isMobileViewport()) {
        return Platform.WEB_MOBILE;
    }
    return Platform.WEB_DESKTOP;
}
/**
 * Retrieves the jurisdiction information from the analytics configuration.
 * @param appInfo - The analytics configuration object.
 * @returns
 */
function getJuridsiction(config) {
    const defaultJurisdiction = {};
    if (!config) {
        return defaultJurisdiction;
    }
    const domain = getCurrentDomain();
    const jurisdictions = JurisdictionsMap[config?.appInfo?.country || ''];
    if (!jurisdictions) {
        return defaultJurisdiction;
    }
    const [jurisdiction] = jurisdictions.filter(j => domain.includes(j.brand));
    if (!jurisdiction) {
        return defaultJurisdiction;
    }
    return jurisdiction;
}
function getDeviceIdOrCreateNew() {
    const existingDeviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (existingDeviceId) {
        return existingDeviceId;
    }
    const newDeviceId = ulid();
    localStorage.setItem(DEVICE_ID_KEY, newDeviceId);
    return newDeviceId;
}
function getSessionIdOrCreateNew() {
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (sessionId) {
        return sessionId;
    }
    return createNewSession();
}
function createNewSession() {
    const newSessionId = ulid();
    sessionStorage.setItem(SESSION_ID_KEY, newSessionId);
    return newSessionId;
}
function isDocumentHidden() {
    const implementation = visibilityImplementations.find(({ hiddenPropertyName }) => (hiddenPropertyName in document));
    return !!document[implementation.hiddenPropertyName];
}
function addVisibilityChangeListener(callback) {
    let visible = true;
    function focused() {
        if (!visible) {
            callback((visible = true));
        }
    }
    function unfocused() {
        if (visible) {
            callback((visible = false));
        }
    }
    const implementation = visibilityImplementations.find(({ hiddenPropertyName }) => (hiddenPropertyName in document));
    if (implementation) {
        visible = !isDocumentHidden();
        document.addEventListener(implementation.eventName, () => {
            if (isDocumentHidden()) {
                unfocused();
            }
            else {
                focused();
            }
        });
    }
}
function addBeforeUnloadListener(callback) {
    window.addEventListener('beforeunload', callback);
}
/**
 * @returns {String} - OS type (windows phone, android, ios, windows, macOS, unknown)
 */
function getOsType() {
    const userAgent = navigator.userAgent || navigator.vendor || '';
    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
        return 'windows phone';
    }
    if (/android/i.test(userAgent)) {
        return 'android';
    }
    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent)) {
        return 'ios';
    }
    if (/windows/i.test(userAgent)) {
        return 'windows';
    }
    if (/Mac OS/i.test(userAgent)) {
        return 'macOS';
    }
    if (/CrOS/i.test(userAgent)) {
        return 'chromeOS';
    }
    if (/linux/i.test(userAgent)) {
        return 'linux';
    }
    return 'unknown';
}
/**
 * Retrieves the version of the web app.
 * @returns An object representing the app version with major, minor, and patch numbers.
 */
function getWebAppVersion() {
    const version = window?.superbetVersion || 'v0.0.0';
    const versionParts = version?.match(/v(\d+)\.(\d+)\.(\d+)/);
    const major = versionParts ? parseInt(versionParts[1]) : 0;
    const minor = versionParts ? parseInt(versionParts[2]) : 0;
    const patch = versionParts ? parseInt(versionParts[3]) : 0;
    return { major, minor, patch };
}
/**
 * Retrieves the current domain name without the .com, .net, etc. suffix.
 * @returns The current domain name as a string.
 */
function getCurrentDomain() {
    try {
        const hostname = window.location.hostname.split('.');
        return hostname.slice(0, hostname.length - 1).join('.');
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return 'superbet';
    }
}
/**
 * Retrieves the app bundle ID based on the country and domain.
 * The bundle ID is in the format of `country.domain.web`.
 * @param country - The country code.
 * @returns
 */
function getAppBundleId(config) {
    const country = config?.appInfo?.country || 'unknown';
    const domain = getCurrentDomain();
    return `${country}.${domain}.web`;
}
/**
 * Gets the last recorded screen visit from the window object.
 * @returns The last recorded screen visit or null if it is not set.
 */
function getLastRecordedScreenVisit() {
    return window.lastRecordedScreenVisit ?? '';
}
/**
 * Retrieves all query params in the url
 * Used only upon analytics init so we can check for organic/non-organic session starts
 * @returns {Object<string, string>|null} An object with key/value pairs of current url query params or null when no query params present
 */
function getQueryParams() {
    const queryParamsList = Array.from(new URLSearchParams(window.location.search).entries());
    if (queryParamsList.length) {
        return Object.fromEntries(queryParamsList.map(([key, value]) => [key, value]));
    }
    return null;
}
// defined by our data analysts - used to classify non-organic visits as deep links
const DEEP_LINK_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign'];
// defined by our data analysts - used to classify non-organic visits as appsflyer
const APPS_FLYER_PARAMS = ['pid', 'c', 'af_channel', 'deep_link_value', 'deep_link_sub1', 'deep_link_sub3', 'af_sub3'];
/**
 * Checks if the query params provided can be classified as deeplink or appsflyer params then maps them accordingly
 * @param queryParams{Object<string, string>}
 * @returns {Object} Proto ready object of known re-engagement query params & session attribution type
 */
function getLinkPayloadFromQueryParams() {
    const queryParams = getQueryParams();
    if (!queryParams) {
        return null;
    }
    const paramKeys = Object.keys(queryParams);
    if (paramKeys.length === 0) {
        return null;
    }
    if (APPS_FLYER_PARAMS.some(param => paramKeys.includes(param))) {
        return {
            ...mapReEngagementEvent(queryParams, true),
            sessionAttributionType: SessionAttributionType.SESSION_ATTRIBUTION_TYPE_AF_LINK,
        };
    }
    if (DEEP_LINK_PARAMS.some(param => paramKeys.includes(param))) {
        return {
            ...mapReEngagementEvent(queryParams),
            sessionAttributionType: SessionAttributionType.SESSION_ATTRIBUTION_TYPE_DEEP_LINK,
        };
    }
    return null;
}
/**
 * Maps url params to the predefined deep link analytics object
 * @param {Object.<string, string>} queryParams
 * @returns {Object} LinkPayload object of known re-engagement query params
 */
function mapReEngagementEvent(queryParams, removeDeepLinkContent = false) {
    return {
        attributionContent: {
            campaign: queryParams.utm_campaign || queryParams.c || null,
            deep_link_content: removeDeepLinkContent ? null : window.location.search || null,
            medium: queryParams.utm_medium || queryParams.af_channel || null,
            promo_code: queryParams.deep_link_sub2 || queryParams.af_sub1 || null,
            referral_params: queryParams.deep_link_sub3 || queryParams.af_sub3 || null,
            share_params: queryParams.deep_link_sub1 || null,
            source: queryParams.utm_source || queryParams.pid || null,
        },
    };
}
/**
 * Retrieves a event by its name
 * @param eventName - Event name on Product Analytics
 * @returns
 */
function getEventByName(eventName) {
    return base_events[eventName] ||
        Object.values(base_events)
            .find((payload) => {
            return payload.name === eventName;
        });
}
function getEventPayloadByName(eventName) {
    const event = getEventByName(eventName);
    return {
        name: event.name,
        action: event.action,
        category: event.category,
        owner: event.owner,
        label: event.label,
    };
}

class ContextManager {
    context;
    constructor(context) {
        this.context = {
            identities: {
                session_id: getSessionIdOrCreateNew(),
                device_id: getDeviceIdOrCreateNew(),
            },
            jurisdiction: {
                brand: '',
                channel: '',
                country: '',
                domain: '',
                licence: '',
            },
            app_bundle_id: '',
            app_version: getWebAppVersion(),
            gdpr_accepted: false,
            platform: getPlatform(),
            // Override the above fields if set in the context
            ...(context ?? {}),
        };
    }
    static getInstance() {
        if (!window.contextManager) {
            window.contextManager = new ContextManager();
        }
        return window.contextManager;
    }
    getContext() {
        return this.context;
    }
    updateContext(attributes) {
        Object.assign(this.context, attributes);
        return this;
    }
}

/**
 * Sinks are being used in order to generically
 * flush data down to multiple analytics providers.
 *
 * Implement this interface and add it to the main
 * list of all sinks, and it will start to consume every
 * event.
 */
class AnalyticsSink {
    isEnabled() {
        // TODO: add killswitch system for specific sinks.
        return true;
    }
}

function isGtmPresent() {
    // We can assume that GTM is present in case we have
    // any data in the dataLayer of the window itself.
    return !!window.dataLayer;
}
function getLastEvent() {
    if (!window.dataLayer) {
        return null;
    }
    return window.dataLayer.length > 0 ? window.dataLayer[window.dataLayer.length - 1] : null;
}
/**
 * Removes the parameters which are automatically propagated in Google Analytics
 * from the last event.
 *
 * This is needed as otherwise we're going to keep sending the same event parameters
 * on each subsequent event, and they will grow in size continuously over time.
 *
 * It goes through the `data` and the last event `data` in order to set
 * every param not present in `data` to undefined, thus clearing it out.
 */
function removeLastEventParameters(data) {
    const lastEvent = getLastEvent();
    if (!lastEvent) {
        return data;
    }
    // These are reserved parameters we should send on every event, as
    // otherwise we won't be able to attribute them.
    const excludedEventFields = [
        'userId',
        'gtm.uniqueEventId',
    ];
    const NO_DATA = undefined;
    const newEventData = [];
    Object.keys(lastEvent).forEach((k) => {
        if (excludedEventFields.includes(k)) {
            return;
        }
        if (lastEvent[k] === NO_DATA) {
            return;
        }
        // This forces the event data to be undefined, which
        // ensures we do not send it at all via GTM.
        newEventData[k] = NO_DATA;
    });
    return {
        ...newEventData,
        ...data,
    };
}
/**
 * Note that this will be a no-op in case GTM is not loaded.
 */
function pushToDataLayer(data) {
    if (!isGtmPresent()) {
        return;
    }
    // Uses setTimeout to ensure that the data is not in the blocking path.
    // Use of this consistenly in this centralised place assures that the
    // ordering is preserved.
    setTimeout(() => {
        window.dataLayer.push(data);
    }, 0);
}
class GoogleAnalytics extends AnalyticsSink {
    name() {
        return 'GoogleAnalytics';
    }
    /**
     * startSessions are not sent to google-analytics at all, as they are
     * generated by GTM directly - this is thus a no-op.
     */
    startSession() {
        pushToDataLayer(ContextManager
            .getInstance()
            .getContext()
            .identities
            .user_id ?? 0);
    }
    /**
     * endSessions are not sent to google-analytics at all, as they are
     * generated by GTM directly - this is thus a no-op.
     */
    endSession() {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
    }
    /**
     * pageView's are not sent to google-analytics at all, as they are
     * generated by GTM directly - this is thus a no-op.
     */
    pageView(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _page_url, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _event) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
    }
    /**
     * Note that this will be a no-op in case GTM is not loaded.
     */
    log(event) {
        // Filter out clickstream events, as they are not relevant for GA.
        if (event.payload.category === 'clickstream') {
            return;
        }
        const sanitizedPayload = flattenObject((event.payload) ?? {});
        const propertiesToDelete = ['name', 'action', 'category', 'label', 'owner'];
        propertiesToDelete.forEach(property => delete sanitizedPayload[property]);
        const eventData = removeLastEventParameters({
            // Transforms the product-data protobuf representation into an object
            // we can send to GTM that it can interpret.
            ...sanitizedPayload,
            // These are required to be able to recognise & categorise the event
            // within GA (if we understand this correctly)
            event: event.payload.name || 'userAction',
            eventAction: (sanitizedPayload.eventAction || event.payload.action) ?? undefined,
            eventCategory: event.payload.category ?? undefined,
            eventLabel: (sanitizedPayload.eventLabel || event.payload.label) ?? undefined,
            eventOwner: event.payload.owner ?? undefined,
            // Merge in *all* identities, note that this will contain
            // `userId` in case we are logged in.
            ...ContextManager.getInstance().getContext().identities,
            // This parameter is being used to recognise events generated
            // by the SDK. That way we can flag events which are generated
            // elsewhere / outside, and chase them down over time.
            eventSentBy: 'superbet-analytics-sdk',
            appVersion: event.context?.app_version,
            timestamp: new Date(event.metadata.timestamp).toISOString(),
        });
        pushToDataLayer(eventData);
    }
    setConfig(_) { }
}

const DEFAULT_BATCH_SIZE = 5;
const DEFAULT_STORAGE_KEY = '_sdk';
/**
 * Represents a batcher for clickstream events.
 * @template T - The type of clickstream event.
 */
class ClickstreamBatcher {
    endpoint;
    maxBatchSize = DEFAULT_BATCH_SIZE;
    unloadedKey;
    batch_events_;
    deferred_events_;
    constructor(options) {
        this.unloadedKey = options.unloadedKey;
        this.batch_events_ = new SessionStorageList(options.batchKey);
        this.deferred_events_ = new SessionStorageList(options.deferredKey);
    }
    /**
     * Sends the deferred events to the server.
     */
    sendDeferredEvents() {
        this.deferred_events_.get()
            .map((event) => this.sendEvent(event));
        this.deferred_events_.clear();
    }
    /**
     * Logs an event to the clickstream.
     * @param event - The event to be logged.
     * @param ignoreVisibility - Optional parameter to ignore document visibility when logging the event.
     */
    sendEvent(event, ignoreVisibility) {
        if (isDocumentHidden() && !ignoreVisibility) {
            this.deferred_events_.push(event);
        }
        else {
            const numberOfEvents = this.batch_events_.push(event);
            const isUnloading = sessionStorage.getItem(this.unloadedKey);
            if (numberOfEvents >= this.maxBatchSize && !isUnloading) {
                this.sendEventsToSnowflake(this.batch_events_.get());
            }
        }
    }
    /**
     * Sends the specified events to the server using the Beacon API.
     *
     * @param events - An array of events to be sent.
     */
    sendEventsToSnowflake(events) {
        if (events.length === 0) {
            return;
        }
        if (!this.endpoint) { // Killswitch to prevent logging if the endpoint is not set
            this.batch_events_.clear(); // not storing localstorage if we are not sending
            return;
        }
        try {
            navigator.sendBeacon(this.endpoint, JSON.stringify(events));
            this.batch_events_.clear();
        }
        catch (error) {
            console.error('Bulk sending failed', error);
        }
    }
    /**
     * Forces the batcher to send all the events to the server.
     */
    forceSendEvents() {
        this.sendDeferredEvents();
        this.sendEventsToSnowflake(this.batch_events_.get());
        this.batch_events_.clear();
    }
}
/**
 * Represents a list stored in the session storage.
 * @template T - The type of elements in the list.
 */
class SessionStorageList {
    constructor(key) {
        this.key_ = key;
    }
    push(element) {
        const elements = this.get();
        elements.push(element);
        this.set(elements);
        return elements.length;
    }
    get() {
        const existing = sessionStorage.getItem(this.key_);
        if (!existing) {
            return [];
        }
        try {
            return JSON.parse(existing);
        }
        catch {
            // Reset in case we can not parse the events
            this.clear();
            return [];
        }
    }
    clear() {
        this.set([]);
    }
    set(elements) {
        sessionStorage.setItem(this.key_, JSON.stringify(elements));
    }
    key_;
}
/**
 * Represents a Clickstream analytics sink.
 * This class extends the `AnalyticsSink` class and provides methods for logging events to the Clickstream analytics sink.
 */
class Clickstream extends AnalyticsSink {
    unloadedKey = `${DEFAULT_STORAGE_KEY}Unloaded`;
    batcher;
    deviceLanguage;
    constructor() {
        super();
        this.batcher = new ClickstreamBatcher({
            unloadedKey: this.unloadedKey,
            batchKey: `${DEFAULT_STORAGE_KEY}Batched`,
            deferredKey: `${DEFAULT_STORAGE_KEY}Deferred`,
        });
    }
    /**
     * Gets the name of the Clickstream analytics sink.
     * @returns The name of the Clickstream analytics sink.
     */
    name() {
        return 'Clickstream';
    }
    /**
     * Logs an event to the Clickstream analytics sink.
     * @param event - The event to log of product.analytics Event type.
     */
    log(event) {
        try {
            this.batcher.sendEvent(attachScreenVisitContext(event));
        }
        catch (error) {
            console.error('Failed to send event', error);
        }
    }
    /**
     * Logs a page view event to the Clickstream analytics sink.
     *  - currently a no-op as there is a different event for this - session_screen_visit
     * @param _page_url - The URL of the page.
     * @param _event - The page view event.
     */
    pageView(_page_url, _event) {
        // noop
    }
    /**
     * Starts a new session.
     *
     * This method checks if the document is hidden and then creates
     * a session start event with the necessary payload, context, and
     * metadata. It logs the event and sends any deferred events in the batcher.
     */
    startSession() {
        const dt = Intl.DateTimeFormat().resolvedOptions();
        const context = ContextManager.getInstance().getContext();
        const sessionStartPayload = createSessionStartPayload(context, this.deviceLanguage, dt.timeZone);
        const sessionStartEvent = createSessionStartEvent(context, sessionStartPayload);
        sessionStorage.removeItem(this.unloadedKey);
        this.log(attachScreenVisitContext(sessionStartEvent));
        this.batcher.sendDeferredEvents();
    }
    /**
     * Ends the current session.
     *
     * This method checks if the session has already ended by checking the
     * session storage. It logs the session end event using the batcher with
     * the `ignoreVisibility` flag set to true.
     */
    endSession() {
        if (!sessionStorage.getItem(this.unloadedKey)) {
            const sessionEndPayload = { name: 'session_end' };
            const sessionEndEvent = {
                payload: { ...sessionEndPayload, ...getEventPayloadByName('session_end') },
                context: ContextManager.getInstance().getContext(),
                metadata: {
                    id: ulid(),
                    timestamp: Number(Date.now()),
                    lastRecordedScreenVisit: getLastRecordedScreenVisit(), // needs unit test coverage
                },
            };
            sessionStorage.setItem(this.unloadedKey, 'true');
            this.batcher.sendEvent(attachScreenVisitContext(sessionEndEvent), true);
            this.batcher.forceSendEvents(); // todo: double check
        }
    }
    /**
     * Sets the configuration for the Clickstream analytics sink.
     * @param config - The analytics configuration.
     */
    setConfig(config) {
        this.deviceLanguage = config?.appInfo?.language || '';
        this.batcher.endpoint = config.endpoints?.clickstream;
        this.batcher.maxBatchSize = config.eventsBatchSize || DEFAULT_BATCH_SIZE;
    }
}
/**
 * Attaches the screen visit context to the event.
 * @param event - The event to attach the screen visit context to.
 * @returns The event with the screen visit context attached.
 */
function attachScreenVisitContext({ payload, context, metadata }) {
    const enrichedMetadata = {
        ...metadata,
        lastRecordedScreenVisit: getLastRecordedScreenVisit(),
    };
    return { payload, context, metadata: enrichedMetadata };
}
/**
 *
 * @param context
 * @param deviceLanguage
 * @param timeZone
 * @returns
 */
const createSessionStartPayload = (context, deviceLanguage, timeZone) => ({
    name: 'session_start',
    appPlatform: context.platform,
    app_country: context.jurisdiction.country,
    device_timezone: timeZone,
    device_language: deviceLanguage,
    device_model: window.navigator.userAgent,
    device_os_type: getOsType(),
    // Optional fields defaulted to empty string
    device_os_version: '',
    device_manufacturer: '',
    device_advertising_id: '',
    app_build_number: '',
});
/**
 * Creates a session start event.
 * @param context - The context of the session.
 * @param sessionStartPayload - The payload of the session start event.
 * @param getIdentities - The function to get the identities.
 * @returns The session start event.
 */
const createSessionStartEvent = (context, sessionStartPayload) => ({
    payload: {
        ...sessionStartPayload,
        ...getEventPayloadByName('session_start'),
    },
    context,
    metadata: {
        id: ulid(),
        timestamp: Number(Date.now()),
    },
});

function default_sinks() {
    return [
        new GoogleAnalytics(),
        new Clickstream(),
    ];
}
class Analytics {
    config;
    sinks_;
    constructor(sinks) {
        this.sinks_ = sinks ?? default_sinks();
    }
    static getInstance() {
        if (!window.analyticsInstance) {
            window.analyticsInstance = new Analytics();
        }
        return window.analyticsInstance;
    }
    // NOTE: should only be used for tests
    static resetInstance_DO_NOT_USE_OUTSIDE_TESTS() {
        window.analyticsInstance = new Analytics();
    }
    setConfig(config) {
        if (!config) {
            return this;
        }
        this.config = config;
        ContextManager.getInstance().updateContext({
            app_bundle_id: getAppBundleId(config),
            jurisdiction: getJuridsiction(config),
        });
        this.sinks_.forEach(sink => sink.setConfig(config));
        return this;
    }
    getConfig() {
        return this.config;
    }
    isEnabled(eventName) {
        // First check the main killswitch
        const killswitchEnabled = this.getConfig()?.killswitch ?? false;
        if (killswitchEnabled) {
            return false;
        }
        // Second check if we have explicitly disabled this event
        const disabledEvents = this.getConfig()?.disabledEvents ?? new Set();
        return !disabledEvents.has(eventName);
    }
    withSinks(sinks) {
        return new Analytics(sinks)
            .setConfig(this.getConfig());
    }
    getSinks() {
        return this.sinks_;
    }
    async pageView(page_url, payload) {
        // We use an artificial event to recognise page-views
        // for now.
        if (!this.isEnabled('pageview')) {
            return;
        }
        const event = {
            // Note that we use artificial event names for
            // page views in order to use the same event structure.
            metadata: {
                // Note that we use ulid (https://www.npmjs.com/package/ulid) as it allows
                // us to sort by the id and retain order of generation, as it includes
                // embedded timestamps within the generation itself.
                id: ulid(),
                timestamp: Number(Date.now()),
            },
            context: ContextManager.getInstance().getContext(),
            payload: {
                ...payload,
                name: 'pageview',
                eventAction: 'view',
            },
        };
        this.sinks_.forEach((sink) => {
            sink.pageView(page_url, event);
        });
    }
    async log(name, payload) {
        if (!this.isEnabled(name)) {
            return;
        }
        const event = {
            metadata: {
                id: ulid(),
                timestamp: Number(Date.now()),
                lastRecordedScreenVisit: getLastRecordedScreenVisit(),
            },
            context: ContextManager.getInstance().getContext(),
            payload: {
                name,
                ...payload,
                ...getEventPayloadByName(name),
            },
        };
        //TODO Log if the context doesn't contain the device_id
        this.sinks_.forEach((sink) => {
            sink.log(event);
        });
        this.updateLastRecordedScreenVisit(name, payload);
    }
    updateLastRecordedScreenVisit(name, payload) {
        if (name === 'session_screen_visit') {
            window.lastRecordedScreenVisit = payload.screen_name;
        }
    }
}

class AnalyticsLifecycleManager {
    identities;
    constructor() {
        this.identities = {
            session_id: getSessionIdOrCreateNew(),
            device_id: getDeviceIdOrCreateNew(),
            user_email: null,
            user_id: null,
            user_uuid: null,
        };
        addVisibilityChangeListener(this.onVisibilityChange.bind(this));
        addBeforeUnloadListener(this.onBeforeUnload.bind(this));
    }
    static getInstance() {
        if (!window.analyticsLifecycleManager) {
            window.analyticsLifecycleManager = new AnalyticsLifecycleManager();
        }
        return window.analyticsLifecycleManager;
    }
    // NOTE: Setter of callback fn that returns user data from the vuex store.
    setUser(getUser) {
        const user = getUser();
        if (user) {
            this.setUserData({
                userId: user.userId,
                userUuid: user.userUuid,
                userEmail: user.email,
            });
        }
        return this;
    }
    setUserData(data) {
        this.setIdentities({
            user_id: data.userId,
            user_uuid: data.userUuid,
            user_email: data.userEmail,
        });
    }
    clearUserData() {
        if (this.identities.user_id || this.identities.user_uuid || this.identities.user_email) {
            this.setIdentities({
                user_id: null,
                user_uuid: null,
                user_email: null,
            });
        }
    }
    // NOTE: While session_id is externally generated, it is always included in the identities.
    getIdentities() {
        return { ...this.identities, session_id: getSessionIdOrCreateNew() };
    }
    setIdentities(attributes) {
        Object.assign(this.identities, attributes);
        ContextManager.getInstance().updateContext({
            identities: this.getIdentities(),
        });
    }
    sessionStart() {
        if (!isDocumentHidden()) {
            this.setIdentities({ session_id: getSessionIdOrCreateNew() });
            Analytics.getInstance().getSinks().forEach(s => s.startSession());
            this.checkForNonOrganicVisit();
        }
    }
    sessionEnd() {
        Analytics.getInstance().getSinks().forEach(s => s.endSession());
    }
    checkForNonOrganicVisit() {
        const mappedQueryParams = getLinkPayloadFromQueryParams();
        if (mappedQueryParams) {
            Analytics.getInstance().log('session_attribution', {
                attribution_content: mappedQueryParams.attributionContent,
                session_attribution_type: mappedQueryParams.sessionAttributionType || SessionAttributionType.SESSION_ATTRIBUTION_TYPE_UNSPECIFIED,
            });
        }
    }
    onVisibilityChange(visible) {
        if (visible) {
            this.sessionStart();
        }
        else {
            this.sessionEnd();
        }
    }
    onBeforeUnload() {
        this.sessionEnd();
    }
}

export { Analytics, AnalyticsLifecycleManager };
//# sourceMappingURL=index.esm.js.map
